import { useCallback, useEffect, useRef, useState } from 'react'

const constraints = { video: true }

export const base64ToFile = (base64String, fileName = "picture") => {
  // Split the base64 string to remove the prefix
  const [prefix, base64Data] = base64String.split(',');

  // Decode the base64 string
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);

  // Determine the MIME type from the prefix (optional, default to 'image/png')
  const mimeType = prefix.match(/:(.*?);/)[1] || 'image/png';

  // Create a Blob from the byte array
  const blob = new Blob([byteArray], { type: mimeType });

  // Create a File from the Blob (optional)
  const file = new File([blob], fileName, { type: mimeType });

  return file;
}

const PhotoCapture = ({ value = null, onChange }) => {
  const ref = useRef()
  const cameraRef = useRef()
  const streamRef = useRef()
  const [showModal, setShowModal] = useState(false)
  const [error, setError] = useState('')
  const [capturedImage, setCapturedImage] = useState(value)

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop()
      })
    }

    if (cameraRef.current?.srcObject) {
      cameraRef.current.srcObject = null
    }
  }, [])

  const startCamera = useCallback(() => {
    setShowModal(true)

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      // MediaDevices API is available
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(function (stream) {
          setError('')

          setTimeout(() => {
            stopCamera()
            if (cameraRef.current) {
              streamRef.current = stream
              cameraRef.current.srcObject = stream
            }
          }, 0)
        })
        .catch(function (error) {
          setError('Camera access required to capture an image.')
          console.error('Error accessing the camera', error.message)
        })
    } else {
      setError('Camera API is not supported by this browser.')
      console.warn('Camera API is not supported by this browser.')
    }
  }, [stopCamera])

  const closeModal = useCallback(() => {
    setShowModal(false)
    setCapturedImage(null)
    stopCamera()
  }, [stopCamera])

  useEffect(() => {
    const handleClickOutside = (event) => {
      const el = ref.current
      if (el && !el.contains(event.target)) {
        closeModal()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [closeModal])

  useEffect(() => {
    if (showModal && !capturedImage) {
      window.addEventListener('blur', stopCamera)
      window.addEventListener('focus', startCamera)
    }
    return () => {
      if (showModal && !capturedImage) {
        window.removeEventListener('blur', stopCamera)
        window.removeEventListener('focus', startCamera)
      }
    }
  }, [startCamera, stopCamera, showModal, capturedImage])

  const captureImage = () => {
    const canvas = document.getElementById('image-capture')
    const context = canvas.getContext('2d')
    const video = cameraRef.current

    // Set the desired cropped width and height
    const cropWidth = 330
    const cropHeight = 330

    // Set the canvas to the cropped dimensions
    canvas.width = cropWidth
    canvas.height = cropHeight

    // Calculate the aspect ratio of the video
    const videoAspectRatio = video.videoWidth / video.videoHeight
    const cropAspectRatio = cropWidth / cropHeight

    let drawWidth, drawHeight, offsetX, offsetY

    if (videoAspectRatio > cropAspectRatio) {
      // Video is wider than the crop area
      drawHeight = cropHeight
      drawWidth = cropHeight * videoAspectRatio
      offsetX = -(drawWidth - cropWidth) / 2
      offsetY = 0
    } else {
      // Video is taller than the crop area
      drawWidth = cropWidth
      drawHeight = cropWidth / videoAspectRatio
      offsetX = 0
      offsetY = -(drawHeight - cropHeight) / 2
    }

    // Flip the context horizontally
    context.translate(cropWidth, 0)   // Move the context's origin to the top-right corner
    context.scale(-1, 1)              // Flip the context horizontally
    context.drawImage(video, offsetX, offsetY, drawWidth, drawHeight)

    const imageDataUrl = canvas.toDataURL('image/png')
    setCapturedImage(imageDataUrl)
    stopCamera()
  }

  const retakePicture = () => {
    setCapturedImage(null)
    startCamera()
  }

  const updatePicture = () => {
    onChange(capturedImage)
    closeModal()
  }

  const editPicture = () => {
    if (value) setCapturedImage(value)
    setShowModal(true)
  }

  return (
    <>
      {value ? (
        <div
          className='saved-picture'
          onClick={editPicture}
        >
          <img
            src={value}
            alt="Screenshot from camera"
          />
        </div>
      ) : (
        <button className="pill" onClick={startCamera}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="14"
            fill="none"
          >
            <path
              fill="#646464"
              fillRule="evenodd"
              d="M0 4.938a1.778 1.778 0 0 1 1.778-1.777h.826a1.778 1.778 0 0 0 1.48-.791l.721-1.085a1.778 1.778 0 0 1 1.48-.791h3.43a1.778 1.778 0 0 1 1.48.791l.721 1.085a1.778 1.778 0 0 0 1.48.79h.826A1.778 1.778 0 0 1 16 4.939v6.79a1.778 1.778 0 0 1-1.778 1.778H1.778A1.778 1.778 0 0 1 0 11.728v-6.79Zm12 2.667a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm-4 2.667a2.665 2.665 0 0 0 2.464-3.687A2.667 2.667 0 1 0 8 10.272Z"
              clipRule="evenodd"
            />
          </svg >
          Use Device Camera
        </button >
      )}
      {
        showModal ? (
          <div className="photo-capture-overlay">
            <div className="photo-capture-modal" ref={ref}>
              <h4>Profile Image</h4>
              <div className='video-wrapper'>
                {error ? <>
                  <div className='camera-placeholder'>
                    <div className='icon-camera'>
                      <svg width="61" height="59" viewBox="0 0 61 59" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M42.48 2.96667L44.9912 6.73963L38.0346 13.6963C35.7654 12.3782 33.1694 11.6667 30.5 11.6667C26.5218 11.6667 22.7064 13.247 19.8934 16.0601C17.0804 18.8731 15.5 22.6884 15.5 26.6667C15.5 28.6365 15.888 30.587 16.6418 32.4069C16.898 33.0255 17.1948 33.6248 17.5296 34.2012L3.83048 47.9003C3.33125 47.6118 2.86742 47.2573 2.45262 46.8425C1.20238 45.5923 0.5 43.8966 0.5 42.1285V16.6667C0.5 14.8986 1.20238 13.2029 2.45262 11.9526C3.70286 10.7024 5.39856 10 7.16667 10H10.2667C11.3639 10.0002 12.4443 9.72948 13.4119 9.21197C14.3795 8.69445 15.2043 7.94611 15.8133 7.03333L18.52 2.96667C19.129 2.05389 19.9539 1.30555 20.9214 0.788034C21.889 0.27052 22.9694 -0.000163637 24.0667 7.42162e-08H36.9333C38.0306 -0.000163637 39.111 0.27052 40.0786 0.788034C41.0461 1.30555 41.871 2.05389 42.48 2.96667ZM30.5 16.6667C31.8071 16.6667 33.1015 16.9229 34.3099 17.4209L21.2542 30.4766C20.7609 29.2795 20.5 27.9865 20.5 26.6667C20.5 24.0145 21.5536 21.471 23.4289 19.5956C25.3043 17.7202 27.8478 16.6667 30.5 16.6667ZM28.2762 36.4163L24.3496 40.3478C24.4853 40.4088 24.6221 40.4678 24.7597 40.5249C26.5796 41.2787 28.5302 41.6667 30.5 41.6667C32.4698 41.6667 34.4204 41.2787 36.2403 40.5249C38.0601 39.771 39.7137 38.6661 41.1066 37.2733C42.4995 35.8804 43.6044 34.2268 44.3582 32.4069C45.112 30.587 45.5 28.6365 45.5 26.6667C45.5 24.5196 45.0397 22.4199 44.1736 20.4997L40.2469 24.4312C40.4147 25.1627 40.5 25.9126 40.5 26.6667C40.5 27.9799 40.2413 29.2802 39.7388 30.4935C39.2363 31.7068 38.4997 32.8092 37.5711 33.7377C36.6425 34.6663 35.5401 35.4029 34.3268 35.9055C33.1136 36.408 31.8132 36.6667 30.5 36.6667C29.7457 36.6667 29.0001 36.5814 28.2762 36.4163ZM54.6148 10.0459L60.2693 4.38459L57.0057 1.12102L2.7445 55.4478L6.00807 58.7114L15.9124 48.7951H53.8333C55.6014 48.7951 57.2971 48.0927 58.5474 46.8425C59.7976 45.5923 60.5 43.8966 60.5 42.1285V16.6667C60.5 14.8986 59.7976 13.2029 58.5474 11.9526C57.4827 10.888 56.0951 10.2206 54.6148 10.0459Z" fill="#AAAAAA" />
                      </svg>
                    </div>
                    <div className='error-message'>{error}</div>
                  </div>
                </> :
                  capturedImage ? (
                    <img src={capturedImage} alt="Screenshot from camera" />
                  ) : (
                    <video id="camera-stream" autoPlay ref={cameraRef}></video>
                  )
                }
              </div>
              <canvas id="image-capture" style={{ display: 'none' }}></canvas>
              <div className="action-btns-wrapper">
                {capturedImage ? (
                  <>
                    {value !== capturedImage && <button type="button" onClick={updatePicture}>Save</button>}
                    <button type="button" onClick={retakePicture}>
                      Retake
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={captureImage} disabled={!!error}>
                    Take Picture
                  </button>
                )}
                <button type="button" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null
      }
    </>
  )
}

export default PhotoCapture
